import { useQuery } from '@tanstack/react-query';
import { useCurrentWarehouseStore } from '../warehouses/currentWarehouse';
import { getPermissions } from './api';
import { useCurrentUser } from './useCurrentUser';

/**
 * Permission checks for the current session, read from `GET /api/v1/auth/permissions`.
 *
 * **Not from the auth cookie, on purpose.** Permissions are checked by the API on every request
 * so a tenant admin can grant or revoke one and have the user pick it up on their next call,
 * with no sign-out — the standing rule since 2026-06-12. Minting them as cookie claims would
 * freeze them until the session was rebuilt, which is the behaviour being avoided. So they come
 * from a route that can be re-fetched, and this query holds them for seconds rather than
 * caching them: `staleTime: 0` means every mount and every explicit refetch asks again.
 *
 * This is for UX only — hiding an action the user cannot perform so they do not meet a 403 they
 * could have been spared. Every endpoint still enforces its own permission server-side via
 * RequirePermission, which is the real boundary. A stale hint therefore cannot grant anything;
 * the worst case is a button that turns out to be refused.
 */

export const PERMISSIONS_KEY = ['auth', 'permissions'] as const;

/**
 * The same list, for one warehouse.
 *
 * Permissions are per warehouse now — the same person can be an operator in one and read-only in
 * another — so the answer is only meaningful alongside the warehouse it was asked about. Keying the
 * cache by it means switching warehouse cannot leave the previous menu on screen, which would offer
 * an action the new warehouse refuses.
 */
export function permissionsKey(warehouseId: string | null) {
  return [...PERMISSIONS_KEY, warehouseId ?? 'none'] as const;
}

export function usePermissions() {
  // Gated on a signed-in user so the anonymous screens (login, registration) do not fire a
  // request that can only come back 401.
  const { data: user } = useCurrentUser();

  // Read from the store rather than passed in: every caller of usePermissions would otherwise
  // have to know about warehouses, and the one that forgot would render a menu for the wrong one.
  const warehouseId = useCurrentWarehouseStore((s) => s.warehouseId);

  const query = useQuery<string[]>({
    queryKey: permissionsKey(warehouseId),
    queryFn: ({ signal }) => getPermissions(signal),
    // NOT gated on a warehouse. It is tempting — with no warehouse chosen the answer carries
    // only unscoped grants, so a scoped user sees a stripped-down menu for one render before the
    // gate settles one. But a brand-new tenant administrator has no warehouse because nobody has
    // created one yet, and gating here left them holding nothing, which hid the very control that
    // creates the first warehouse. A tenant administrator's permissions do not depend on a
    // warehouse anyway; the server short-circuits them.
    enabled: Boolean(user),
    // Always considered stale: a permission granted a minute ago must be visible now.
    staleTime: 0,
    retry: false,
  });

  const granted = new Set(query.data ?? []);

  // "We could not find out" is not the same as "you may do nothing", and conflating them is
  // severe: it silently strips navigation from a user who has every permission. So on error
  // this FAILS OPEN — every check answers true, the UI shows what it would normally show, and
  // the server refuses anything the user genuinely lacks.
  //
  // Safe because this hook is a hint and never a boundary: every endpoint re-checks its own
  // permission per request, so the worst case is a control that turns out to be refused. The
  // opposite default is not safe in the way it looks — it grants nothing extra, and costs the
  // user their app.
  //
  // An empty list from a SUCCESSFUL call is different and is honoured: that user really does
  // hold nothing.
  const unknown = query.isError;

  return {
    /** True when the session carries this code, or when the list could not be loaded. */
    has: (code: string) => unknown || granted.has(code),
    /** Every code the session holds. Empty when unknown — check {@link isUnknown} first. */
    granted,
    /** True while the list is still being fetched — treat actions as unavailable. */
    isLoading: query.isPending && Boolean(user),
    /**
     * The list could not be loaded, so nothing here is authoritative. Callers that filter a
     * list rather than asking `has` need this, otherwise they would filter against an empty
     * set and hide everything.
     */
    isUnknown: unknown,
    /**
     * Ask the server again. Called when the navigation menu is expanded, matching how the
     * other products in this family behave: the menu is retrieved on open rather than held
     * from earlier in the session.
     */
    refetch: query.refetch,
  };
}
